'use client';

import { OrganizationInviteData } from '../types/organization.type';
import { useRouter } from 'next/navigation';
import { logger } from '@/core/logger';

const STORAGE_KEY = 'pending_org_invite';

export const organizationInviteStorage = {
	/**
	 * Saves organization invite data to localStorage
	 */
	save(data: OrganizationInviteData): void {
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
		} catch (error) {
			logger.error('Failed to save invite data', error);
		}
	},

	/**
	 * Retrieves organization invite data from localStorage
	 * Returns null if not found or parsing fails
	 */
	get(): OrganizationInviteData | null {
		try {
			const stored = localStorage.getItem(STORAGE_KEY);
			if (!stored) return null;

			return JSON.parse(stored) as OrganizationInviteData;
		} catch (error) {
			logger.error('Failed to parse invite data', error);
			// Auto-cleanup corrupted data
			this.clear();
			return null;
		}
	},

	/**
	 * Removes organization invite data from localStorage
	 */
	clear(): void {
		localStorage.removeItem(STORAGE_KEY);
	},

	/**
	 * Extracts invite data from URL search params
	 */
	extractFromSearchParams(
		searchParams: URLSearchParams
	): OrganizationInviteData | null {
		const inviteCode = searchParams.get('invite_code');
		const orgName = searchParams.get('org_name');
		const email = searchParams.get('email');

		if (!inviteCode) return null;

		return {
			inviteCode,
			organizationName: orgName,
			invitedEmail: email,
		};
	},

	/**
	 * Removes invite parameters from URL without page reload
	 * @internal - Exported for testing
	 */
	_processUrlCleanup(href: string): string | null {
		const url = new URL(href);
		const hadParams =
			url.searchParams.has('invite_code') ||
			url.searchParams.has('org_name') ||
			url.searchParams.has('email');

		if (!hadParams) return null;

		url.searchParams.delete('invite_code');
		url.searchParams.delete('org_name');
		url.searchParams.delete('email');
		return url.pathname + url.search;
	},

	/**
	 * Removes invite parameters from URL without page reload
	 */
	cleanupUrlParams(router: ReturnType<typeof useRouter>): void {
		if (typeof window === 'undefined') return;

		const newUrl = this._processUrlCleanup(window.location.href);
		if (newUrl) {
			router.replace(newUrl);
		}
	},
};
